import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronUp, CheckCircle, Send, Star, Check } from 'lucide-react';
import { submitForm } from '../lib/formSubmissions';
import { notifyCleoOfFormSubmission } from '../utils/formSubmissionHelper';
import { useNavigate } from 'react-router-dom';

const ConversionCopyCommand: React.FC = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    currentChallenges: '',
    projectType: '',
    timeline: '',
    otherServices: '',
    brandVoice: '',
    competitors: '',
    additionalInfo: ''
  });

  const handleBackToHome = () => {
    try {
      navigate('/');
    } catch (error) {
      console.log('Navigate failed, using window.location');
      window.location.href = '/';
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Submit to Supabase backend
      const result = await submitForm({
        formType: 'conversion_copy',
        email: formData.email,
        name: formData.name,
        formData: {
          company: formData.company,
          website: formData.website,
          currentChallenges: formData.currentChallenges,
          projectType: formData.projectType,
          timeline: formData.timeline,
          otherServices: formData.otherServices,
          brandVoice: formData.brandVoice,
          competitors: formData.competitors,
          additionalInfo: formData.additionalInfo
        }
      });

      if (result.success) {
        // Notify Cleo about the successful submission
        notifyCleoOfFormSubmission('conversion_copy', {
          email: formData.email,
          name: formData.name,
          company: formData.company,
          website: formData.website,
          projectType: formData.projectType,
          timeline: formData.timeline,
          currentChallenges: formData.currentChallenges,
          brandVoice: formData.brandVoice,
          competitors: formData.competitors,
          otherServices: formData.otherServices,
          additionalInfo: formData.additionalInfo
        });
        
        // Show success message
        alert('Thank you for your request! We\'ll be in touch within 24 hours.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          company: '',
          website: '',
          currentChallenges: '',
          projectType: '',
          timeline: '',
          otherServices: '',
          brandVoice: '',
          competitors: '',
          additionalInfo: ''
        });
      } else {
        throw new Error(result.error || 'Failed to submit form');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    const formElement = document.getElementById('copy-audit-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const pricingTiers = [
    {
      name: 'Homepage Copy',
      price: '$1,500+',
      duration: '2-3 days',
      icon: '🖥️',
      popular: false,
      description: 'Your homepage is the digital essence of your brand. We craft sophisticated, conversion-focused copy that captivates and invites engagement.',
      features: [
        'Investment in Long Term Brand Authority',
        'Bespoke Headline Crafting',
        'Refined Subheadings',
        'Expertly Curated Body Copy',
        'Strategic Conversion Copy',
        'Tailored Tone and Voice Alignment',
        'Optimized for Engagement and Impact'
      ]
    },
    {
      name: 'Product Descriptions',
      price: 'Starting at $50 per product',
      duration: '1-2 days',
      icon: '📦',
      popular: false,
      description: 'Your products deserve words that inspire. We write compelling descriptions that evoke desire and showcase their unique value.',
      features: [
        'Immersive Storytelling',
        'Emotionally Engaging Copy',
        'Sensory Language to Spark Imagination',
        'Tailored Brand Voice Integration/consistency',
        'Persuasive and Elegant Language',
        'Incorporation of Social Proof Elements',
        'Persuasive Call to Action'
      ]
    },
    {
      name: 'Email Campaign Sequence',
      price: 'Starting at $500 for a 3-email sequence',
      duration: '3-5 days',
      icon: '✉️',
      popular: true,
      description: 'Email sequences that build trust, drive action, and nurture lasting relationships with your audience.',
      features: [
        'Emotionally Engaging Subject Lines',
        'Tailored Storytelling',
        'Compelling Copy That Converts',
        'Elevated Persuasion with Subtlety',
        'Premium Design Integration',
        'Refined Timing and Cadence',
        'Optimized for Maximum Engagement',
        'Template formatting included'
      ]
    },
    {
      name: 'Ad Copy & Social Media',
      price: 'Starting at $500 per ad set',
      duration: '1-2 days',
      icon: '📣',
      popular: false,
      description: 'Cut through the noise with ad copy and social media content designed to engage, convert, and elevate your brand\'s presence.',
      features: [
        'Attention-Grabbing Headlines and Hooks',
        'Targeted Emotional Appeal',
        'Tailored to Platform',
        'Audience specific messaging',
        'Visually Compelling Copy',
        'A/B Testing and Refinement',
        'Optimized for Engagement'
      ]
    },
    {
      name: 'Brand Voice Guidelines',
      price: 'Starting at $3,000',
      duration: '5-7 days',
      icon: '🎤',
      popular: false,
      description: 'Your brand\'s voice defines its identity. We create Brand Voice Guidelines that ensure your tone is consistent, refined, and unmistakably yours, resonating with your audience at every touchpoint.',
      features: [
        'Defining Your Brand\'s Unique Voice',
        'Tone and Language Refinement',
        'Comprehensive Brand Vocabulary',
        'Guidelines for Messaging Across Channels',
        'Consistency Across Teams and Content Creators',
        'Emotional and Psychological Impact',
        'Adapting for Different Campaigns',
        'Clarity in Communication'
      ]
    },
    {
      name: 'Copy Audit',
      price: 'Starting at $500',
      duration: '2-3 days',
      icon: '🔍',
      popular: false,
      description: 'Every word matters in branding. Our Copy Audit service meticulously reviews your content, aligning it with your brand\'s standards and ensuring it drives results.',
      features: [
        'Comprehensive Content Review',
        'Brand Voice Alignment',
        'Strategic Conversion Recommendations',
        'Emotional Impact Assessment',
        'SEO Optimization',
        'Detailed Reporting & Actionable Insights',
        'Grammar & Syntax Refinement'
      ]
    },
    {
      name: 'Sales Funnel',
      price: '$2,500',
      duration: '5-7 days',
      icon: '💰',
      popular: false,
      description: 'A sales funnel is a journey, not just steps. We craft captivating, persuasive sales funnel copy that moves your audience with elegance, guiding them from interest to commitment.',
      features: [
        'Custom Funnel Strategy',
        'High-Converting Landing Pages',
        'Compelling Lead Magnet Copy',
        'Nurturing Email Sequences',
        'Objection Handling Copy',
        'Powerful Call-to-Actions',
        'Conversion Optimization',
        'Exclusivity & Urgency'
      ]
    },
    {
      name: 'Full Website Copy',
      price: '$4,000-10,000+',
      duration: '10-14 days',
      icon: '✨',
      popular: false,
      description: 'Your website is the essence of your brand. We craft Full Website Copy that transforms your online presence into an elegant experience, guiding visitors with sophistication from first impression to final call to action.',
      features: [
        'Complete website rewrite',
        'Strategic Lead Conversion',
        'Persuasive Emotional Appeal',
        'Bespoke Homepage Copy',
        'Compelling About Us Page',
        'Persuasive Service & Product Pages',
        'Strategic Calls-to-Action',
        'SEO-Optimized Copy'
      ]
    }
  ];

  const beforeAfterExamples = [
    {
      before: "Affordable web design for small businesses.",
      after: "Get a Website That Works. Without Breaking the Bank. Affordable Web Design for Small Businesses That Drives Results"
    },
    {
      before: "Click here to get started.",
      after: "Let\'s Build Your Dream Website"
    },
    {
      before: "We offer the best marketing services.",
      after: "Boost Your Business with Proven Marketing Services That Deliver."
    }
  ];

  const testimonials = [
    {
      name: 'J.M.',
      title: 'Tech Startup Founder',
      quote: "I gave them a simple outline, and they delivered a landing page that feels like a $10K funnel. The transformation is incredible"
    },
    {
      name: 'L.H',
      title: 'Luxury E-commerce Brand Owner',
      quote: "We swapped in their copy, and click-throughs spiked by 38%. The ROI was immediate and undeniable."
    },
    {
      name: 'E.M',
      title: 'Agency Partner',
      quote: "This is the most confident I\'ve felt about our brand message in years. The clarity and impact are unmatched."
    }
  ];

  const faqs = [
    {
      question: "What types of copywriting services do you offer?",
      answer: "We specialize in a variety of copywriting services, including: Website copy, Sales pages & landing pages, Blog posts & articles, Email sequences, Ad copy, Product descriptions, Copy audits & revisions"
    },
    {
      question: "How long does it take to receive my copy?",
      answer: "Once we\'ve defined the project details and received payment, you can expect your first draft within 1-3 business days. If refinements are necessary, those will be completed shortly after receiving your feedback."
    },
    {
      question: "Do you offer revisions?",
      answer: "Yes! We include two round of refinements with each project to ensure your copy aligns perfectly with your vision and goals. Additional rounds of revisions can be discussed and priced separately if needed."
    },
    {
      question: "What if I don\'t have a website yet or only sell on social media (like TikTok or Instagram)?",
      answer: "No problem! If you don\'t have a traditional website, feel free to share your social media links (TikTok, Instagram, etc.) or online shop links. We can tailor your copy to fit your specific platform or audience."
    },
    {
      question: "How do you ensure my brand\'s voice is captured in the copy?",
      answer: "We take the time to understand your brand, your values, and your target audience. Through collaboration and clear communication, we ensure your voice shines through in every piece of copy we create. If you have any existing guidelines or examples, that helps us align even more closely with your vision."
    },
    {
      question: "What if I only want part of the service? For example, just a section of the copy, not the entire thing?",
      answer: "We understand that your needs may be specific, and we pride ourselves on offering tailored solutions. Whether you\'re looking for a single landing page, select product descriptions, a portion of your email sequence, or an individual element of your sales funnel, we\'re here to adapt the scope to suit your exact requirements. You won\'t be charged for unnecessary services. We provide a proportionate pricing structure based on the content you require. Simply share the sections or components you need, and we will offer a personalized quote for that scope."
    },
    {
      question: "What happens if I\'m not satisfied with the copy?",
      answer: "We understand how important it is to get your copy just right, which is why we offer two rounds of revisions to ensure every detail aligns with your vision. And to give you even more flexibility, we provide multiple options for you to choose from, so you\'re never limited to just one direction. Our goal is to deliver work that not only meets, but exceeds your expectations, with a personalized approach that\'s as refined and tailored as your brand."
    },
    {
      question: "Do you offer ongoing copywriting support after the project is completed?",
      answer: "Absolutely. At Manifest Illusions, we believe in fostering enduring partnerships with our clients. While we don\'t offer subscription-based plans, we remain at your service for any future needs. Whether you require occasional product descriptions, tailored email campaigns, or additional copy for new initiatives, we\'re here to ensure your brand\'s voice stays as compelling and relevant as ever, on a project-by-project basis."
    }
  ];

  const processSteps = [
    "Complete the brief to provide us with essential details about your project. Your goals, challenges, and what kind of copy you need.",
    "After reviewing your submission, we\'ll reach out to you promptly to start a conversation and clarify any additional details. This is the time to discuss your vision and expectations.",
    "Together, we\'ll define the specifics of your project, whether that\'s improving existing copy or creating new messaging. We\'ll finalize the goals, tone, and key elements that need to be addressed.",
    "Once the project details are confirmed, we\'ll send you an invoice or payment link. After payment is received, we\'ll move forward with crafting your copy.",
    "We\'ll send you your professionally written copy. You\'ll have a couple rounds of refinements included if necessary, ensuring it aligns with your vision and goals.",
    "After any refinements are made (if applicable), you\'ll receive the final version of your copy, ready for use on your website, in emails, or wherever it\'s needed."
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <button 
            onClick={handleBackToHome}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors cursor-pointer px-4 py-2 rounded-md hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-6xl font-light mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Copy That Sells. Clarity That Converts.
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
           Every word meticulously crafted to captivate attention, build unshakable trust, and ignite action that speaks to the heart. A journey of persuasion and connection, designed to resonate deeply and convert with intention, across every moment, every touchpoint.
          </motion.p>
          
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToForm}
            className="bg-yellow-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-yellow-700 transition-all shadow-lg"
            type="button"
          >
         Let us alleviate your stress
          </motion.button>
          
          {/* Scroll Cue */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-16"
          >
            <div className="w-px h-16 bg-gradient-to-b from-gray-300 to-transparent mx-auto"></div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Select the Service That Aligns with Your Vision.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Each service is thoughtfully tailored to your needs, combining expert strategy and creative precision. All packages include revisions, ensuring your copy is not only aligned with your brand but crafted to resonate deeply with your audience and drive lasting results.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-2 ${
                  tier.popular 
                    ? 'border-yellow-600 ring-4 ring-yellow-100' 
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-yellow-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                      <Star className="w-3 h-3 fill-current" />
                      <span>Most Popular</span>
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className="text-4xl mb-3">{tier.icon}</div>
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {tier.name}
                  </h3>
                  <div className="text-3xl font-bold text-gray-900 mb-1">{tier.price}</div>
                  <div className="text-sm text-gray-500">Delivered in {tier.duration}</div>
                </div>

                <p className="text-gray-600 text-sm mb-6 leading-relaxed">
                  {tier.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={scrollToForm}
                  className={`w-full py-3 px-4 rounded-lg font-medium transition-all ${
                    tier.popular
                      ? 'bg-yellow-600 text-white hover:bg-yellow-700 shadow-lg'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                  type="button"
                >
                  Get Started
                </motion.button>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="text-gray-600 mb-4">Need something custom or have questions about pricing?</p>
            <button
              onClick={scrollToForm}
              className="text-yellow-600 hover:text-yellow-700 font-medium underline"
              type="button"
            >
              Let\'s discuss your specific needs
            </button>
          </motion.div>
        </div>
      </section>

      {/* Differentiator Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-light mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Clients Choose Us Over Freelancers
            </h2>
            
            <div className="text-left max-w-3xl mx-auto mb-8">
              <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              While others settle for words, we build the blueprint for your success. Every sentence is a strategic move; every paragraph, a calculated step designed to captivate, convert, and elevate your brand.<br />
              </p>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
               We do not just write, we craft experiences that tap into human desire, mastering the art of persuasion and the science of conversion. No fluff. No guesswork. Just precision and purpose in every line.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {[
                'Built for Unmatched Conversion',
                'Brand-Consistent & Tone-Perfect',
                'Delivered Swiftly & Strategically',
                'Grounded in Proven Frameworks'
              ].map((point, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-center space-x-3"
                >
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
             How We Amplify Your Message and Drive Real Results.
            </h2>
          </motion.div>

          <div className="space-y-8">
            {beforeAfterExamples.map((example, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="grid md:grid-cols-2 gap-8 items-center"
              >
                <div className="bg-gray-100 p-8 rounded-lg border-l-4 border-gray-400">
                  <div className="text-sm font-medium text-gray-500 mb-2">BEFORE</div>
                  <p className="text-lg text-gray-600 italic">"{example.before}"</p>
                </div>
                
                <div className="bg-yellow-50 p-8 rounded-lg border-l-4 border-yellow-600">
                  <div className="text-sm font-medium text-yellow-700 mb-2">AFTER</div>
                  <p className="text-lg text-gray-900 font-medium">"{example.after}"</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="text-center text-gray-600 mt-12 text-lg italic"
          >
            Copy That Cuts Through the Noise. Clear, Clean, and Convincing, Right Now.
          </motion.p>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              How It Works
            </h2>
          </motion.div>

          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-start space-x-4"
              >
                <div className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center font-semibold flex-shrink-0">
                  {index + 1}
                </div>
                <p className="text-lg text-gray-700 pt-1">{step}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Clients Who\'ve Seen Results
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-lg shadow-sm border border-gray-100"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center font-semibold text-gray-600">
                    {testimonial.name}
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.title}</div>
                  </div>
                </div>
                <p className="text-gray-700 italic leading-relaxed">"{testimonial.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-light mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Frequently Asked Questions
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg border border-gray-100 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                  type="button"
                >
                  <span className="text-lg font-medium text-gray-900">{faq.question}</span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </button>
                
                {openFaq === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-6"
                  >
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Copy Audit Form Section */}
      <section id="copy-audit-form" className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl font-light mb-12 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Your Website Doesn\'t Need More Words. It Needs the Right Ones.
          </motion.h2>
          
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                  Website URL
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleFormChange}
                  placeholder="https://your-website.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors"
                />
              </div>
            </div>

            <div>
              <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                What type of copy do you need? *
              </label>
              <select
                id="projectType"
                name="projectType"
                required
                value={formData.projectType}
                onChange={handleFormChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors"
              >
                <option value="">Select project type...</option>
                <option value="landing-page">HomePage Copy</option>
                <option value="product-descriptions">Product Descriptions</option>
                <option value="ad-copy">Email Campaign Sequence</option>
                <option value="email-campaigns">Ad Copy & Social Media</option>
                <option value="brand-voice">Brand Voice Guidelines</option>
                <option value="website-audit">Copy Audit</option>
                <option value="sales-page">Sales Funnel</option>
                <option value="brand-makeover">Full Website Copy</option>
                <option value="other">Other (please specify below)</option>
              </select>
            </div>

            <div>
              <label htmlFor="currentChallenges" className="block text-sm font-medium text-gray-700 mb-2">
                What are your current copy challenges? *
              </label>
              <textarea
                id="currentChallenges"
                name="currentChallenges"
                required
                rows={4}
                value={formData.currentChallenges}
                onChange={handleFormChange}
                placeholder="e.g., Low conversion rates, unclear messaging, brand voice inconsistency..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors resize-vertical"
              />
            </div>

            <div>
              <label htmlFor="brandVoice" className="block text-sm font-medium text-gray-700 mb-2">
                Brand Voice/Personality
              </label>
              <textarea
                id="brandVoice"
                name="brandVoice"
                rows={3}
                value={formData.brandVoice}
                onChange={handleFormChange}
                placeholder="e.g., Professional yet approachable, edgy and bold, warm and trustworthy, innovative and tech-forward..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors resize-vertical"
              />
            </div>

            <div>
              <label htmlFor="competitors" className="block text-sm font-medium text-gray-700 mb-2">
                Competitors or Brands You Admire
              </label>
              <textarea
                id="competitors"
                name="competitors"
                rows={3}
                value={formData.competitors}
                onChange={handleFormChange}
                placeholder="e.g., Apple, Slack, Mailchimp, your direct competitors, or any brands whose messaging style you admire..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors resize-vertical"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                  Project Timeline
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleFormChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors"
                >
                  <option value="">Select timeline...</option>
                  <option value="asap">ASAP (Rush job)</option>
                  <option value="1-week">Within 1 week</option>
                  <option value="2-weeks">Within 2 weeks</option>
                  <option value="1-month">Within 1 month</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
              
              <div>
                <label htmlFor="otherServices" className="block text-sm font-medium text-gray-700 mb-2">
                  Are you interested in other services?
                </label>
                <textarea
                  id="otherServices"
                  name="otherServices"
                  rows={3}
                  value={formData.otherServices}
                  onChange={handleFormChange}
                  placeholder="Full Website Copy, Sales Funnel, Product Descriptions, etc."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors resize-vertical"
                />
              </div>
            </div>

            <div>
              <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                id="additionalInfo"
                name="additionalInfo"
                rows={3}
                value={formData.additionalInfo}
                onChange={handleFormChange}
                placeholder="Any specific requirements, brand guidelines, or additional context..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent transition-colors resize-vertical"
              />
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              className={`w-full px-8 py-4 rounded-lg text-lg font-medium transition-all shadow-lg flex items-center justify-center space-x-2 ${
                isSubmitting 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
              }`}
            >
              <Send className="w-5 h-5" />
              <span>{isSubmitting ? 'Submitting...' : 'Unveil Your Copy\'s Potential'}</span>
            </motion.button>
          </motion.form>
        </div>
      </section>
    </div>
  );
};

export default ConversionCopyCommand;